import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  DEFAULT_SITE_CONTENT,
  FooterContent,
  HeroContent,
  ImpactStatsContent,
  JourneyContent,
  ProductsPageContent,
  SiteContentService,
  WhyChooseUsContent,
} from '../../../core/services/site-content.service';

type ContentSectionId = 'hero' | 'journey' | 'productsPage' | 'whyChooseUs' | 'impactStats' | 'footer';

interface ContentTab {
  id: ContentSectionId;
  label: string;
}

@Component({
  selector: 'app-website-content',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './website-content.component.html',
  styleUrls: ['./website-content.component.scss'],
})
export class WebsiteContentComponent implements OnInit, OnDestroy {
  readonly tabs: ContentTab[] = [
    { id: 'hero', label: 'Hero' },
    { id: 'journey', label: 'Journey' },
    { id: 'productsPage', label: 'Products Page' },
    { id: 'whyChooseUs', label: 'Why Choose Us' },
    { id: 'impactStats', label: 'Impact Stats' },
    { id: 'footer', label: 'Footer' },
  ];

  activeTab: ContentSectionId = 'hero';
  isSaving = false;
  isSeeding = false;
  successMessage = '';
  errorMessage = '';

  readonly heroForm = this.fb.group({
    eyebrow: [DEFAULT_SITE_CONTENT.hero.eyebrow],
    headline: [DEFAULT_SITE_CONTENT.hero.headline],
    subHeadline: [DEFAULT_SITE_CONTENT.hero.subHeadline],
    description: [DEFAULT_SITE_CONTENT.hero.description],
    primaryButtonText: [DEFAULT_SITE_CONTENT.hero.primaryButtonText],
    secondaryButtonText: [DEFAULT_SITE_CONTENT.hero.secondaryButtonText],
    heroImageUrl: [DEFAULT_SITE_CONTENT.hero.heroImageUrl],
  });

  readonly journeyForm = this.fb.group({
    eyebrow: [DEFAULT_SITE_CONTENT.journey.eyebrow],
    title: [DEFAULT_SITE_CONTENT.journey.title],
    description: [DEFAULT_SITE_CONTENT.journey.description],
    buttonText: [DEFAULT_SITE_CONTENT.journey.buttonText],
    steps: this.fb.array(DEFAULT_SITE_CONTENT.journey.steps.map((step) => this.fb.control(step))),
  });

  readonly productsPageForm = this.fb.group({
    eyebrow: [DEFAULT_SITE_CONTENT.productsPage.eyebrow],
    headline: [DEFAULT_SITE_CONTENT.productsPage.headline],
    description: [DEFAULT_SITE_CONTENT.productsPage.description],
  });

  readonly whyChooseUsForm = this.fb.group({
    eyebrow: [DEFAULT_SITE_CONTENT.whyChooseUs.eyebrow],
    title: [DEFAULT_SITE_CONTENT.whyChooseUs.title],
    description: [DEFAULT_SITE_CONTENT.whyChooseUs.description],
  });

  readonly impactStatsForm = this.fb.group({
    title: [DEFAULT_SITE_CONTENT.impactStats.title],
    stats: this.fb.array(
      DEFAULT_SITE_CONTENT.impactStats.stats.map((stat) =>
        this.fb.group({
          value: [stat.value],
          label: [stat.label],
        }),
      ),
    ),
  });

  readonly footerForm = this.fb.group({
    companyDescription: [DEFAULT_SITE_CONTENT.footer.companyDescription],
    phone: [DEFAULT_SITE_CONTENT.footer.phone],
    email: [DEFAULT_SITE_CONTENT.footer.email],
    address: [DEFAULT_SITE_CONTENT.footer.address],
    facebook: [DEFAULT_SITE_CONTENT.footer.facebook],
    instagram: [DEFAULT_SITE_CONTENT.footer.instagram],
    linkedin: [DEFAULT_SITE_CONTENT.footer.linkedin],
    youtube: [DEFAULT_SITE_CONTENT.footer.youtube],
  });

  private readonly subscriptions = new Subscription();
  private hasSeededOnOpen = false;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly fb: FormBuilder,
    private readonly siteContentService: SiteContentService,
  ) {}

  ngOnInit(): void {
    void this.seedDefaultContentOnOpen();
    this.bindSection<HeroContent>('hero', this.heroForm);
    this.bindSection<JourneyContent>('journey', this.journeyForm);
    this.bindSection<ProductsPageContent>('productsPage', this.productsPageForm);
    this.bindSection<WhyChooseUsContent>('whyChooseUs', this.whyChooseUsForm);
    this.bindSection<ImpactStatsContent>('impactStats', this.impactStatsForm);
    this.bindSection<FooterContent>('footer', this.footerForm);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get journeySteps(): FormArray<FormControl<string | null>> {
    return this.journeyForm.get('steps') as FormArray<FormControl<string | null>>;
  }

  get impactStats(): FormArray<FormGroup> {
    return this.impactStatsForm.get('stats') as FormArray<FormGroup>;
  }

  setActiveTab(tabId: ContentSectionId): void {
    this.activeTab = tabId;
    this.successMessage = '';
    this.errorMessage = '';
  }

  async saveActiveSection(): Promise<void> {
    const sectionId = this.activeTab;
    const form = this.getForm(sectionId);

    this.isSaving = true;
    this.successMessage = '';
    this.errorMessage = '';

    try {
      await this.siteContentService.updateSection(sectionId, this.normalizePayload(sectionId, form.getRawValue()));
      this.successMessage = 'Content saved successfully.';
    } catch (error) {
      console.error(`[WebsiteContent] Unable to save ${sectionId} content`, error);
      this.errorMessage = this.getErrorMessage(error, 'Unable to save website content.');
    } finally {
      this.isSaving = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  resetActiveSection(): void {
    this.patchForm(this.activeTab, DEFAULT_SITE_CONTENT[this.activeTab]);
    this.successMessage = 'Fallback content restored in the form. Save to publish it.';
    this.errorMessage = '';
  }

  async seedDefaults(): Promise<void> {
    this.isSeeding = true;
    this.successMessage = '';
    this.errorMessage = '';

    try {
      await this.siteContentService.seedDefaultContent();
      this.successMessage = 'Missing default content has been seeded.';
    } catch (error) {
      console.error('[WebsiteContent] Unable to seed default content', error);
      this.errorMessage = this.getErrorMessage(error, 'Unable to seed default content.');
    } finally {
      this.isSeeding = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  trackByTab(_: number, tab: ContentTab): string {
    return tab.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private bindSection<T extends object>(sectionId: ContentSectionId, form: FormGroup): void {
    this.subscriptions.add(
      this.siteContentService.getSection<T>(sectionId).subscribe((content) => {
        if (content) {
          this.patchForm(sectionId, content);
          form.markAsPristine();
          this.changeDetectorRef.detectChanges();
        }
      }),
    );
  }

  private async seedDefaultContentOnOpen(): Promise<void> {
    if (this.hasSeededOnOpen) {
      return;
    }

    this.hasSeededOnOpen = true;

    try {
      await this.siteContentService.seedDefaultContent();
    } catch (error) {
      console.error('[WebsiteContent] Unable to seed default content', error);
    }
  }

  private getForm(sectionId: ContentSectionId): FormGroup {
    return {
      hero: this.heroForm,
      journey: this.journeyForm,
      productsPage: this.productsPageForm,
      whyChooseUs: this.whyChooseUsForm,
      impactStats: this.impactStatsForm,
      footer: this.footerForm,
    }[sectionId];
  }

  private patchForm(sectionId: ContentSectionId, content: any): void {
    if (sectionId === 'journey') {
      this.replaceStringArray(this.journeySteps, this.toSixItems(content.steps, DEFAULT_SITE_CONTENT.journey.steps));
    }

    if (sectionId === 'impactStats') {
      this.replaceStatsArray(content.stats || DEFAULT_SITE_CONTENT.impactStats.stats);
    }

    this.getForm(sectionId).patchValue(content);
  }

  private replaceStringArray(formArray: FormArray<FormControl<string | null>>, values: string[]): void {
    formArray.clear();
    values.forEach((value) => formArray.push(this.fb.control(value)));
  }

  private replaceStatsArray(stats: Array<{ value?: string; label?: string }>): void {
    this.impactStats.clear();
    this.toFourStats(stats).forEach((stat) => {
      this.impactStats.push(
        this.fb.group({
          value: [stat.value],
          label: [stat.label],
        }),
      );
    });
  }

  private normalizePayload(sectionId: ContentSectionId, value: any): any {
    if (sectionId === 'journey') {
      return {
        ...value,
        steps: this.toSixItems(value.steps, DEFAULT_SITE_CONTENT.journey.steps),
      };
    }

    if (sectionId === 'impactStats') {
      return {
        ...value,
        stats: this.toFourStats(value.stats),
      };
    }

    return value;
  }

  private toSixItems(values: unknown, fallback: string[]): string[] {
    const items = Array.isArray(values) ? values : fallback;
    return Array.from({ length: 6 }, (_, index) => String(items[index] || fallback[index] || '').trim());
  }

  private toFourStats(values: unknown): Array<{ value: string; label: string }> {
    const fallback = DEFAULT_SITE_CONTENT.impactStats.stats;
    const items = Array.isArray(values) ? values : fallback;

    return Array.from({ length: 4 }, (_, index) => ({
      value: String(items[index]?.value || fallback[index]?.value || '').trim(),
      label: String(items[index]?.label || fallback[index]?.label || '').trim(),
    }));
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
  }
}
